import axios from 'axios';

import { SourceCamera, SourceConfig } from '../types';

type WaveSyncServer = {
  id: string;
  name: string;
  ownerAccountEmail: string;
  version: string;
};

type WaveSyncCamera = {
  id: string;
  name: string;
  ip: string;
  model: string;
  vendor: string;
  group: string;
};

async function waveSyncPost(
  url: string,
  body: Record<string, string>
): Promise<any> {
  const { data } = await axios.post(url, body);
  return data;
}

export const hanwhaWaveConfig: SourceConfig = {
  label: 'Hanwha WAVE',
  description: 'Import cameras from Hanwha WAVE via Wave Sync',
  credentialFields: [
    {
      name: 'username',
      label: 'Wave Sync Username',
      type: 'text',
      required: true,
    },
    {
      name: 'password',
      label: 'Wave Sync Password',
      type: 'password',
      required: true,
    },
    {
      name: 'stream_mode',
      label: 'Stream Type',
      type: 'select',
      required: true,
      defaultValue: 'cloud',
      options: [{ label: 'Cloud stream', value: 'cloud' }],
      helpText:
        'Cloud streams are accessible from anywhere. Local streams must be imported from Wave via the Gateway web interface.',
    },
  ],
  qualityOptions: [
    { label: 'Low', value: 'lo' },
    { label: 'High', value: 'hi' },
  ],
  defaultQuality: 'lo',

  async fetchServers(
    credentials: Record<string, string>,
    apiBaseUrl: string
  ) {
    const { username, password } = credentials;

    const servers: WaveSyncServer[] = await waveSyncPost(
      `${apiBaseUrl}/integrations/wavesync/servers`,
      { user: username, password }
    );

    if (!servers.length) {
      throw new Error('No Wave Sync servers found for this account.');
    }

    return servers.map((s) => ({
      id: s.id,
      name: s.name,
      description: s.ownerAccountEmail,
    }));
  },

  async fetchCameras(
    credentials: Record<string, string>,
    apiBaseUrl: string,
    serverId?: string
  ): Promise<SourceCamera[]> {
    const { username, password } = credentials;

    if (!serverId) {
      throw new Error('Please select a server first.');
    }

    const cameras: WaveSyncCamera[] = await waveSyncPost(
      `${apiBaseUrl}/integrations/wavesync/cameras`,
      { user: username, password, server: serverId }
    );

    return cameras
      .map((cam) => ({
        id: cam.id,
        name: cam.name || cam.id,
        location: cam.group || '',
        model: cam.model || undefined,
        manufacturer: cam.vendor || undefined,
        ip: cam.ip || undefined,
        serverId,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  },

  buildStreamUri(
    camera: SourceCamera,
    credentials: Record<string, string>,
    quality: string,
    apiBaseUrl: string
  ): string {
    const serverId = camera.serverId;

    const params = new URLSearchParams({
      server: `${serverId}.relay.vmsproxy.com`,
      camera_id: camera.id,
      user: credentials.username,
      password: credentials.password,
      [quality]: 'true',
    });

    return `${apiBaseUrl}/integrations/wavesync/stream?${params}`;
  },

  buildStreamName(camera: SourceCamera, quality: string): string {
    const suffix = quality === 'hi' ? '' : ' (low)';
    return `${camera.name}${suffix}`;
  },
};
